import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/admin/_layout/inventory/inspection-frequencies"
)({
  component: RouteComponent,
});

function RouteComponent() {
  // InspectionFrequencyViewSet's read_permission ("inventory.stock.view")
  // gates list/retrieve too, with no degrade-to-own-rows fallback, so the
  // whole page is protected up front; write_permission
  // ("inventory.item.manage") gates just the write UI within.
  return (
    <ProtectedRoute requiredPermission="inventory.stock.view">
      <InspectionFrequenciesPage />
    </ProtectedRoute>
  );
}

import { useState } from "react";
import { toast } from "sonner";
import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchInspectionFrequencies,
  useInspectionFrequencyMutations,
} from "@/hooks/api/inventory/useCollectionsLabels";
import { InspectionFrequencyTable } from "@/components/inventory/tables/InspectionFrequencyTable";
import { InspectionFrequencyFormSheet } from "@/components/inventory/forms/InspectionFrequencyFormSheet";
import type {
  InspectionFrequency,
  InspectionFrequencyPayload,
} from "@/types/inventory";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function InspectionFrequenciesPage() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "inventory.item.manage");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const frequenciesQuery = useFetchInspectionFrequencies({
    page_size: pageSize,
    page,
  });
  const frequencies = frequenciesQuery.data?.results ?? [];
  const count = frequenciesQuery.data?.count ?? 0;

  const { createFrequency, updateFrequency, deleteFrequency } =
    useInspectionFrequencyMutations();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<InspectionFrequency | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InspectionFrequency | null>(
    null
  );

  const isMutating =
    createFrequency.isPending ||
    updateFrequency.isPending ||
    deleteFrequency.isPending;
  const isLoading = frequenciesQuery.isPending || isMutating;
  const isFetching = frequenciesQuery.isFetching || isMutating;

  const handleSubmit = async (data: InspectionFrequencyPayload) => {
    if (editing) {
      await updateFrequency.mutateAsync({ id: editing.id, payload: data });
      toast.success("Inspection frequency updated");
    } else {
      await createFrequency.mutateAsync(data);
      toast.success("Inspection frequency created");
    }
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteFrequency.mutateAsync({ id: deleteTarget.id });
      toast.success("Inspection frequency deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Inspection Frequencies"
          description="Set recurring inspection schedules (e.g. 6 Monthly, LOLER, IRATA)"
          onAdd={
            canManage
              ? () => {
                  setEditing(null);
                  setIsViewOnly(false);
                  setIsSheetOpen(true);
                }
              : undefined
          }
          addLabel="Add Frequency"
          isLoading={isFetching}
        />

        <InspectionFrequencyTable
          frequencies={frequencies}
          isLoading={isLoading}
          onView={(f) => {
            setEditing(f);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(f) => {
            setEditing(f);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(f) => setDeleteTarget(f)}
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <InspectionFrequencyFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          editing={editing}
          isLoading={isMutating}
          isViewOnly={isViewOnly}
        />

        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Frequency"
          description={`Delete inspection frequency "${deleteTarget?.name}"?`}
          itemName={deleteTarget?.name}
          isLoading={deleteFrequency.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
